import { useCallback, useEffect, useState } from 'react';

function readStored<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Generic hook that syncs React state with localStorage.
 *
 * On initialization, attempts to read and parse the stored value for `key`.
 * If reading/parsing fails (storage unavailable, malformed JSON, etc.), falls
 * back to `defaultValue`. When `key` changes (e.g. a user-scoped key after an
 * account switch) the value is re-read from the new key instead of carrying
 * the previous key's value over.
 *
 * On every state change, attempts to write the serialized value back to
 * localStorage. If writing fails (storage full, unavailable, etc.), the
 * error is swallowed so the app continues to function without persistence.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [entry, setEntry] = useState<{ key: string; value: T }>(() => ({
    key,
    value: readStored(key, defaultValue),
  }));

  let current = entry;
  if (entry.key !== key) {
    // Derived-state reset during render (React-sanctioned pattern).
    current = { key, value: readStored(key, defaultValue) };
    setEntry(current);
  }

  useEffect(() => {
    try {
      localStorage.setItem(current.key, JSON.stringify(current.value));
    } catch {
      // Storage full or unavailable — degrade gracefully, no persistence.
    }
  }, [current.key, current.value]);

  const setValue = useCallback((value: T): void => {
    setEntry({ key, value });
  }, [key]);

  return [current.value, setValue];
}
